import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { figmaPxToWidthPercent, Report, useGetScalingSize } from '@ui/Report';
import { ColorShades } from '@shared/helpers/color.helpers';

/** Former `.08vw` at 1920px viewport — ~1.5px gap on the report frame. */
const BAR_SEGMENT_GAP = figmaPxToWidthPercent(1.536);

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  backgroundColor?: string;
  barWidthPercent?: number;
}>()((_theme, { getScalingSize, backgroundColor, barWidthPercent }) => ({
  barChart: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    backgroundColor: backgroundColor ?? 'transparent',
    aspectRatio: '1000/12',
  },
  barSegments: {
    overflow: 'hidden',
    display: 'flex',
    gap: getScalingSize(BAR_SEGMENT_GAP),
    flexBasis: `${barWidthPercent}%`,
  },
  barSegment: {
    transition: 'width 0.3s ease',
  },
}));

type BarSegment = {
  id: string;
  label: string;
  value: number;
  sizePercent: number;
};

export type StackedBarGraphData = {
  /** assumes all segment sizes sum up to 100% */
  segments: BarSegment[];
};

type BarChartStylingProps = {
  totalWidthPercent?: number;
  backgroundColor?: string;
  colorShades: ColorShades;
};
export type BarChartProps = Pretty<StackedBarGraphData & BarChartStylingProps>;

export const HorizontalStackedBarChart: React.FC<BarChartProps> = ({
  totalWidthPercent = 100,
  segments,
  backgroundColor,
  colorShades,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({
    getScalingSize,
    backgroundColor,
    barWidthPercent: totalWidthPercent,
  });
  if (segments.length === 0) {
    return (
      <Report.Text as="span" color="text.subtle" fontStyle="italic">
        No data available
      </Report.Text>
    );
  }
  return (
    <Box className={classes.barChart}>
      <Box className={classes.barSegments}>
        {segments.map((segment) => (
          <Box
            key={segment.id}
            className={classes.barSegment}
            title={`${segment.label}: ${segment.value}`}
            sx={{
              width: `${segment.sizePercent}%`,
              backgroundColor: colorShades.getById(segment.id),
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
